import { Controller } from "@hotwired/stimulus"
import { debug } from '../utils/debug'
import Splide from "@splidejs/splide"

/**
 * Product Gallery Controller
 * Uses Splide's native sync() for main + thumbnail carousel
 * Images load normally (optimized, small files)
 * Videos defer loading until their slide becomes visible
 *
 * https://splidejs.com/tutorials/thumbnail-carousel/
 */
export default class extends Controller {
  static targets = ["main", "thumbnails"]

  connect() {
    if (!this.hasMainTarget) return

    this.mainSplide = new Splide(this.mainTarget, {
      rewind: true,
      pagination: false,
      arrows: false,
    })

    // Listen for slide changes to initialize videos when they become visible
    this.mainSplide.on('active', this.handleSlideActive.bind(this))

    if (this.hasThumbnailsTarget) {
      this.thumbnailSplide = new Splide(this.thumbnailsTarget, {
        fixedWidth: 80,
        fixedHeight: 80,
        gap: 8,
        rewind: true,
        pagination: false,
        isNavigation: true,
        breakpoints: {
          576: {
            fixedWidth: 60,
            fixedHeight: 60,
            gap: 6,
          },
        },
      })

      this.mainSplide.sync(this.thumbnailSplide)
      this.mainSplide.mount()
      this.thumbnailSplide.mount()
    } else {
      this.mainSplide.mount()
    }
  }

  disconnect() {
    this.mainSplide?.destroy()
    this.thumbnailSplide?.destroy()
  }

  /**
   * Handle when a slide becomes active - initialize videos
   */
  handleSlideActive(slideComponent) {
    const slide = slideComponent.slide
    this.initializeVideoInSlide(slide)
  }

  /**
   * Initialize a video player within a specific slide
   * Videos with data-lazy-video="true" defer loading until visible
   */
  async initializeVideoInSlide(slide) {
    const lazyVideo = slide.querySelector('[data-lazy-video="true"]')
    if (!lazyVideo || lazyVideo.dataset.videoInitialized === 'true') return

    // Mark as initialized to prevent duplicate initialization
    lazyVideo.dataset.videoInitialized = 'true'

    // Find the video element
    const video = lazyVideo.querySelector('video')
    if (!video) return

    // Check if this is a Shaka-enabled video (HLS/DASH)
    if (video.dataset.hlsUrl || video.dataset.dashUrl) {
      // Dynamically import Shaka initializer and initialize
      try {
        const { initShakaElement } = await import('common/shaka_player')
        await initShakaElement(video)
      } catch (error) {
        debug.warn('product-gallery', '[ProductGallery] Failed to initialize Shaka video:', error)
        // Fallback to native video loading
        if (video.preload === 'none') {
          video.preload = 'metadata'
          video.load()
        }
      }
    } else if (video.preload === 'none') {
      // Standard video - just start loading
      video.preload = 'metadata'
      video.load()
    }
  }
}
